/** Matches `ActionButton` variants used by delete confirm (AV-53 v1.2). */
export type CustomerDeleteConfirmPrimaryVariant = "contained-primary" | "contained-error";

export const CUSTOMER_FALLBACK_LABEL = "This customer";

export function resolveCustomerDisplayName(
  companyLegalName: string | null | undefined,
  name: string | null | undefined,
  fallback: string = CUSTOMER_FALLBACK_LABEL,
): string {
  return companyLegalName?.trim() || name?.trim() || fallback;
}

export function resolveActiveBookingsCount(activeBookingsCount: number | null | undefined): number {
  if (typeof activeBookingsCount !== "number" || !Number.isFinite(activeBookingsCount)) {
    return 0;
  }
  return Math.max(0, Math.floor(activeBookingsCount));
}

/** Shared success toast for status deactivate and delete→deactivate (D-AV53-010). */
export function buildCustomerDeactivatedNotification(customerName: string): {
  variant: "success";
  title: string;
  description: string;
} {
  const name = customerName.trim() || CUSTOMER_FALLBACK_LABEL;
  return {
    variant: "success",
    title: "Customer deactivated",
    description: `${name} is now inactive.`,
  };
}

export function buildCustomerDeleteConfirmCopy(params: {
  companyName: string;
  activeBookingsCount: number;
}): {
  title: string;
  primaryLabel: string;
  primaryActionVariant: CustomerDeleteConfirmPrimaryVariant;
  descriptionParts: {
    lead: string;
    boldCount: string;
    mid: string;
    deactivateNote: string;
  };
} {
  const name = params.companyName.trim() || CUSTOMER_FALLBACK_LABEL;
  const count = resolveActiveBookingsCount(params.activeBookingsCount);
  const hasActiveBookings = count > 0;
  const bookingWord = count === 1 ? "active booking" : "active bookings";
  return {
    title: hasActiveBookings ? "This customer has active bookings" : "Delete customer",
    primaryLabel: hasActiveBookings ? "Deactivate customer" : "Delete customer",
    primaryActionVariant: hasActiveBookings ? "contained-primary" : "contained-error",
    descriptionParts: {
      lead: `${name} has `,
      boldCount: hasActiveBookings ? `${count} ${bookingWord}` : "no active bookings",
      mid: ". Deleting their account will delete their profile from the scheduler, and they will no longer be able to log in. You will not be able to create future bookings for them or recover their account.",
      deactivateNote: hasActiveBookings
        ? "If you want to keep the account data, you can deactivate their account instead and reactivate them anytime."
        : "",
    },
  };
}
